/**
 * Copyright 2022-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { createColumnHelper } from "@tanstack/react-table";
import { theme } from "@nordcloud/gnui";
import { EventStatus, IncomingEventStatus } from "~/generated/graphql";
import { ExpandIcon } from "~/components";
import { MediumIconColumn, SmallIconColumn } from "~/components/Tables/styles";
import { EntityType, PlanEntityIncomingEventList } from "../types";
import { TooltipIconStatus } from "./TooltipIconStatus";
import { getTotalResourceCount } from "./utils";

const columnHelper = createColumnHelper<PlanEntityIncomingEventList>();

type Props = {
  status: EventStatus | IncomingEventStatus;
};
export function planActionsColumns({ status }: Props) {
  return [
    columnHelper.accessor(() => null, {
      header: "",
      id: "dummy",
      meta: SmallIconColumn,
      cell: ({ row }) => {
        return row.getCanExpand() ? (
          <ExpandIcon
            name="chevronDown"
            title="chevronDown"
            isExpanded={row.getIsExpanded()}
            onClick={row.getToggleExpandedHandler()}
          />
        ) : null;
      },
    }),
    columnHelper.accessor(() => null, {
      header: "",
      id: "state",
      cell: ({ row }) => {
        return (
          <div
            style={{
              paddingLeft: `${row.depth * 4}rem`,
            }}
          >
            {row.getCanExpand() ? (
              <TooltipIconStatus
                status={status}
                type={EntityType.FUTURE_ACTION_BATCH}
                actions={row.original.batchActions ?? []}
              />
            ) : (
              <TooltipIconStatus status={status} type={EntityType.ACTION} />
            )}
          </div>
        );
      },
      meta: MediumIconColumn,
    }),
    columnHelper.accessor(() => null, {
      header: "Action Name",
      cell: ({ row }) => {
        return (
          <div
            style={{
              paddingLeft: `${row.depth * 2.5}rem`,
            }}
          >
            {row.original.name}
          </div>
        );
      },
      meta: {
        css: {
          width: "100%",
          minWidth: "20rem",
          padding: theme.spacing.spacing03,
        },
      },
    }),
    columnHelper.accessor(() => null, {
      header: "Number of resources",
      cell: ({ row }) => {
        const count = getTotalResourceCount(row.original.resourceGroups);
        return <div>{count === undefined || count === 0 ? "-" : count}</div>;
      },
      meta: {
        css: {
          minWidth: "8.75rem",
          padding: theme.spacing.spacing00,
        },
      },
    }),
    columnHelper.accessor(() => null, {
      header: "Successful",
      cell: () => "-",
      meta: {
        css: {
          minWidth: "8.75rem",
          padding: theme.spacing.spacing00,
        },
      },
    }),
    columnHelper.accessor(() => null, {
      header: "Unsuccessful",
      cell: () => "-",
      meta: {
        css: {
          minWidth: "8.75rem",
          padding: theme.spacing.spacing03,
        },
      },
    }),
  ] as never;
}
